/**
 * The distinct, upper-cased FOREIGN currencies in `fromCurrencies` — i.e. the
 * set that actually needs a rate lookup (the presentation currency translates
 * by identity). Exported so the web hook can build its query key from the same
 * normalization the fetch performs.
 */
export function uniqueForeignCurrencies(fromCurrencies, toCurrency) {
    return [
        ...new Set(fromCurrencies.map((c) => c.toUpperCase()).filter((c) => c !== toCurrency.toUpperCase())),
    ];
}
/**
 * Fetch exchange rates for multiple currencies in one batch, for translating
 * financial statements into a presentation currency.
 *
 * A per-currency failure is NOT fatal: the currency is recorded in
 * `missingCurrencies` (and `allRatesFound` goes false) so the report can
 * disclose the gap instead of failing to render.
 */
export async function fetchBatchTranslationRates(client, { fromCurrencies, toCurrency, asOfDate }) {
    const uniqueCurrencies = uniqueForeignCurrencies(fromCurrencies, toCurrency);
    const dateString = asOfDate ?? new Date().toISOString().split('T')[0];
    const rates = {};
    const ratesArray = [];
    const missingCurrencies = [];
    // Add identity rate for presentation currency
    rates[toCurrency.toUpperCase()] = {
        fromCurrency: toCurrency.toUpperCase(),
        toCurrency: toCurrency.toUpperCase(),
        rate: 1,
        rateDate: dateString,
    };
    // Fetch rates for each unique currency
    await Promise.all(uniqueCurrencies.map(async (fromCurrency) => {
        try {
            const { data, error } = await client.rpc('convert_currency', {
                p_amount: 1, // Get rate by converting 1 unit
                p_from_currency: fromCurrency,
                p_to_currency: toCurrency.toUpperCase(),
                p_date: dateString,
            });
            if (error) {
                console.warn(`Failed to get rate for ${fromCurrency}: ${error.message}`);
                missingCurrencies.push(fromCurrency);
                return;
            }
            if (!data || data.length === 0) {
                console.warn(`No rate returned for ${fromCurrency}`);
                missingCurrencies.push(fromCurrency);
                return;
            }
            const rateInfo = {
                fromCurrency,
                toCurrency: toCurrency.toUpperCase(),
                rate: data[0].exchange_rate,
                rateDate: data[0].rate_date,
            };
            rates[fromCurrency] = rateInfo;
            ratesArray.push(rateInfo);
        }
        catch (err) {
            console.warn(`Error fetching rate for ${fromCurrency}:`, err);
            missingCurrencies.push(fromCurrency);
        }
    }));
    return {
        rates,
        ratesArray,
        allRatesFound: missingCurrencies.length === 0,
        missingCurrencies,
    };
}
/**
 * Translate an amount from one currency to another using pre-fetched rates
 *
 * @param asOfDate Optional `YYYY-MM-DD` reporting date, reported as `rateDate`
 *   in the identity (same currency) and missing-rate branches — those have no
 *   fetched rate row to take a date from. Falls back to the identity entry the
 *   batch hook stores under the presentation currency, then to today.
 */
export function translateAmount(amount, fromCurrency, rates, toCurrency, asOfDate) {
    const fromCurrencyUpper = fromCurrency.toUpperCase();
    const toCurrencyUpper = toCurrency.toUpperCase();
    const fallbackRateDate = asOfDate ?? rates[toCurrencyUpper]?.rateDate ?? new Date().toISOString().split('T')[0];
    // Same currency - no translation needed
    if (fromCurrencyUpper === toCurrencyUpper) {
        return {
            translatedAmount: amount,
            rate: 1,
            rateDate: fallbackRateDate,
        };
    }
    const rateInfo = rates[fromCurrencyUpper];
    if (!rateInfo) {
        // Rate not found - return original amount with warning
        console.warn(`No rate found for ${fromCurrencyUpper}, using 1:1`);
        return {
            translatedAmount: amount,
            rate: 1,
            rateDate: fallbackRateDate,
        };
    }
    return {
        translatedAmount: amount * rateInfo.rate,
        rate: rateInfo.rate,
        rateDate: rateInfo.rateDate,
    };
}
//# sourceMappingURL=currencyTranslation.js.map